import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BLOG_URL, DOCS_URL } from '@app/config';
import { JsonLdService, MetaService } from '@app/core';
import {
  BlogPost,
  BlogSectionComponent,
  CustomerLogoSectionComponent,
  TestimonialsSectionComponent,
  VideoSectionComponent,
} from '@app/widgets';
import { NATIVE_BUILDS_FAQS } from '../../constants';
import { NativeBuildsAndroidSectionComponent } from '../native-builds-android-section/native-builds-android-section.component';
import { NativeBuildsCliSectionComponent } from '../native-builds-cli-section/native-builds-cli-section.component';
import { NativeBuildsCtaSectionComponent } from '../native-builds-cta-section/native-builds-cta-section.component';
import { NativeBuildsFaqSectionComponent } from '../native-builds-faq-section/native-builds-faq-section.component';
import { NativeBuildsFeaturesSectionComponent } from '../native-builds-features-section/native-builds-features-section.component';
import { NativeBuildsHeroSectionComponent } from '../native-builds-hero-section/native-builds-hero-section.component';
import { NativeBuildsHowItWorksSectionComponent } from '../native-builds-how-it-works-section/native-builds-how-it-works-section.component';
import { NativeBuildsIntegrationsSectionComponent } from '../native-builds-integrations-section/native-builds-integrations-section.component';
import { NativeBuildsIosSectionComponent } from '../native-builds-ios-section/native-builds-ios-section.component';
import { NativeBuildsSecuritySectionComponent } from '../native-builds-security-section/native-builds-security-section.component';
import { NativeBuildsStatisticsSectionComponent } from '../native-builds-statistics-section/native-builds-statistics-section.component';

@Component({
  selector: 'app-native-builds',
  imports: [
    BlogSectionComponent,
    CustomerLogoSectionComponent,
    NativeBuildsAndroidSectionComponent,
    NativeBuildsCliSectionComponent,
    NativeBuildsCtaSectionComponent,
    NativeBuildsFaqSectionComponent,
    NativeBuildsFeaturesSectionComponent,
    NativeBuildsHeroSectionComponent,
    NativeBuildsHowItWorksSectionComponent,
    NativeBuildsIntegrationsSectionComponent,
    NativeBuildsIosSectionComponent,
    NativeBuildsSecuritySectionComponent,
    NativeBuildsStatisticsSectionComponent,
    TestimonialsSectionComponent,
    VideoSectionComponent,
  ],
  templateUrl: './native-builds.component.html',
  styleUrl: './native-builds.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NativeBuildsComponent {
  protected readonly blogPosts: BlogPost[] = [
    {
      title: 'Announcing Capawesome Cloud Native Builds',
      description:
        'We are excited to introduce Capawesome Cloud Native Builds, a new feature that allows you to build your Capacitor apps directly in the cloud without the need for local development environments.',
      date: 'November 17, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${BLOG_URL}announcing-capawesome-cloud-native-builds/`,
      tag: 'Product Update',
    },
    {
      title: 'Setup Native Builds with Capawesome Cloud',
      description:
        'Learn how to configure and use Native Builds in Capawesome Cloud to streamline your iOS and Android app development process.',
      date: 'November 17, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${DOCS_URL}native-builds/setup/`,
      tag: 'Development',
    },
    {
      title: 'Understanding Build Stacks in Capawesome Cloud',
      description:
        'Explore the different build stacks available in Capawesome Cloud and how they can optimize your native app builds for performance and compatibility.',
      date: 'November 5, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${DOCS_URL}native-builds/build-stacks/`,
      tag: 'Development',
    },
  ];

  constructor(
    private readonly metaService: MetaService,
    private readonly jsonLdService: JsonLdService,
  ) {
    this.metaService.setDescription(
      'Build native iOS and Android apps in the cloud with optimized build stacks, automatic signing, and seamless CI/CD integration for Capacitor projects.',
    );
    this.metaService.setTitle('Native Builds for Capacitor');
    this.metaService.setKeywords([
      'Capacitor native builds',
      'cloud builds',
      'iOS builds',
      'Android builds',
      'CI/CD',
    ]);
    this.jsonLdService.setStructuredData([
      JsonLdService.buildWebPageSchema(
        'Native Builds for Capacitor - Capawesome Cloud',
        'Build native iOS and Android apps in the cloud with optimized build stacks and seamless CI/CD integration.',
        'https://cloud.capawesome.io/native-builds/',
      ),
      JsonLdService.buildFaqSchema(NATIVE_BUILDS_FAQS),
    ]);
  }
}
